import React, { useState, useEffect, useRef, useCallback } from 'react';
import { RefreshCw, Search, Play, Pause, ChevronDown, ShieldAlert } from 'lucide-react';
import * as adminService from '../services/adminService';
import * as clusterService from '../services/clusterService';

const LOG_LEVELS = ['ALL', 'DEBUG', 'INFO', 'WARNING', 'ERROR'];
const POLL_INTERVALS = [
  { label: '3s', ms: 3000 },
  { label: '5s', ms: 5000 },
  { label: '10s', ms: 10000 },
];

const AdminLogsPage = () => {
  const [tab, setTab] = useState('server');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState('ALL');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [pollInterval, setPollInterval] = useState(5000);
  const [clusters, setClusters] = useState([]);
  const [selectedCluster, setSelectedCluster] = useState('');
  const [forbidden, setForbidden] = useState(false);
  const logEndRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    fetchClusters();
    fetchLogs();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [tab, selectedCluster, levelFilter]);

  useEffect(() => {
    if (autoRefresh) {
      pollRef.current = setInterval(fetchLogs, pollInterval);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [autoRefresh, pollInterval, tab, selectedCluster, levelFilter, search]);

  const fetchClusters = async () => {
    try {
      const res = await clusterService.listClusters();
      if (res.success) setClusters(res.clusters || []);
    } catch (err) {
      console.error('Failed to fetch clusters:', err);
    }
  };

  const fetchLogs = useCallback(async () => {
    try {
      let res;
      if (tab === 'server') {
        res = await adminService.getServerLogs({
          lines: 500,
          level: levelFilter !== 'ALL' ? levelFilter : null,
          search: search || null,
        });
      } else {
        if (!selectedCluster) {
          setLogs([]);
          setLoading(false);
          return;
        }
        res = await adminService.getClusterLogs(selectedCluster, { lines: 500 });
      }
      if (res.success) {
        setLogs(res.lines || []);
      } else if (res.forbidden) {
        setForbidden(true);
        setAutoRefresh(false);
      } else {
        setLogs([{ text: `Error: ${res.error}`, level: 'ERROR' }]);
      }
    } catch (err) {
      setLogs([{ text: `Failed to fetch logs: ${err.message}`, level: 'ERROR' }]);
    } finally {
      setLoading(false);
    }
  }, [tab, selectedCluster, levelFilter, search]);

  useEffect(() => {
    if (logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  const getLevelColor = (level) => {
    switch (level?.toUpperCase()) {
      case 'ERROR': return 'text-red-400';
      case 'WARNING': return 'text-yellow-400';
      case 'INFO': return 'text-green-400';
      case 'DEBUG': return 'text-gray-500';
      default: return 'text-gray-300';
    }
  };

  const parseLogLine = (line) => {
    if (typeof line === 'object') return line;
    // Try to extract log level from common formats
    const match = line.match(/\b(DEBUG|INFO|WARNING|ERROR|CRITICAL)\b/i);
    return { text: line, level: match ? match[1].toUpperCase() : null };
  };

  if (forbidden) {
    return (
      <div className="max-w-md mx-auto text-center py-20">
        <ShieldAlert size={40} className="mx-auto text-gray-400 mb-4" />
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Admins only</h2>
        <p className="text-sm text-gray-500">
          Orchestrator logs are restricted to administrators.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Orchestrator Logs</h2>
      </div>

      {/* Tabs */}
      <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          onClick={() => setTab('server')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            tab === 'server' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Server Logs
        </button>
        <button
          onClick={() => setTab('cluster')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            tab === 'cluster' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Cluster Logs
        </button>
      </div>

      {/* Controls */}
      <div className="flex items-center space-x-3 flex-wrap gap-y-2">
        {tab === 'cluster' && (
          <select
            value={selectedCluster}
            onChange={(e) => setSelectedCluster(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">Select cluster...</option>
            {clusters.map(c => (
              <option key={c.cluster_name || c.name} value={c.cluster_name || c.name}>
                {c.cluster_name || c.name} ({c.status})
              </option>
            ))}
          </select>
        )}

        {tab === 'server' && (
          <>
            <div className="relative flex-1 max-w-xs">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search logs..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchLogs()}
                className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <select
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {LOG_LEVELS.map(l => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </>
        )}

        <button
          onClick={fetchLogs}
          className="flex items-center space-x-1 px-3 py-2 border rounded-lg text-sm hover:bg-gray-50"
        >
          <RefreshCw size={14} />
          <span>Refresh</span>
        </button>

        <div className="flex items-center space-x-2 border rounded-lg px-3 py-2">
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`${autoRefresh ? 'text-green-600' : 'text-gray-400'}`}
          >
            {autoRefresh ? <Pause size={14} /> : <Play size={14} />}
          </button>
          <span className="text-xs text-gray-500">Auto</span>
          {autoRefresh && (
            <select
              value={pollInterval}
              onChange={(e) => setPollInterval(Number(e.target.value))}
              className="text-xs border-none focus:ring-0 p-0 bg-transparent"
            >
              {POLL_INTERVALS.map(p => (
                <option key={p.ms} value={p.ms}>{p.label}</option>
              ))}
            </select>
          )}
        </div>

        <span className="text-xs text-gray-400">{logs.length} lines</span>
      </div>

      {/* Log output */}
      <div className="bg-gray-900 rounded-lg shadow border border-gray-700 overflow-hidden">
        <div className="overflow-auto max-h-[70vh] p-4 font-mono text-xs leading-5">
          {loading ? (
            <div className="text-gray-500 text-center py-8">Loading logs...</div>
          ) : logs.length === 0 ? (
            <div className="text-gray-500 text-center py-8">
              {tab === 'cluster' && !selectedCluster
                ? 'Select a cluster to view its logs.'
                : 'No logs available.'}
            </div>
          ) : (
            logs.map((line, i) => {
              const parsed = parseLogLine(line);
              return (
                <div key={i} className={`whitespace-pre-wrap ${getLevelColor(parsed.level)} hover:bg-gray-800`}>
                  {parsed.text}
                </div>
              );
            })
          )}
          <div ref={logEndRef} />
        </div>
      </div>
    </div>
  );
};

export default AdminLogsPage;
